import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { LineItemsTable } from "@/features/invoices/LineItemsTable";
import { trpc } from "@/lib/trpc";
import { formatDollars } from "@payrollmart-invoice/shared";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, FileText } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/invoices/$invoiceId")({
	component: InvoiceDetailPage,
});

const statusStyles: Record<string, string> = {
	draft: "bg-[#eef2ff] text-[#4f46e5]",
	approved: "bg-[#f0fdf4] text-[#15803d]",
	pushed: "bg-[#fffbeb] text-[#d97706]",
	paid: "bg-[#f0fdf4] text-[#15803d]",
	skipped: "bg-[#f3f4f6] text-[#6b7280]",
};

function InvoiceDetailPage() {
	const { invoiceId } = Route.useParams();
	const { data: invoice, isLoading } = trpc.invoices.getById.useQuery({ id: invoiceId });
	const approveMutation = trpc.invoices.approve.useMutation();
	const skipMutation = trpc.invoices.skip.useMutation();
	const deleteMutation = trpc.invoices.delete.useMutation();
	const pushMutation = trpc.qbo.pushInvoice.useMutation();
	const { data: qboStatus } = trpc.qbo.status.useQuery();
	const utils = trpc.useUtils();

	// QBO already-billed warning is async — we need to await the user's
	// decision after the history fetch completes. Track it as a separate
	// confirmation state with a promise resolver so handlePushToQbo can
	// `await pushDecision.then(...)`.
	const [pushWarning, setPushWarning] = useState<{
		message: string;
		resolve: (proceed: boolean) => void;
	} | null>(null);

	if (isLoading) {
		return (
			<div className="space-y-4">
				<div className="h-8 w-48 animate-pulse rounded bg-[#f3f4f6]" />
				<div className="h-64 animate-pulse rounded-lg bg-[#f3f4f6]" />
			</div>
		);
	}

	if (!invoice) {
		return <div className="py-12 text-center text-sm text-[#6b7280]">Invoice not found.</div>;
	}

	async function handleApprove() {
		try {
			await approveMutation.mutateAsync({ invoiceId });
			await utils.invoices.getById.invalidate({ id: invoiceId });
			toast.success("Invoice approved");
		} catch (error) {
			toast.error(error instanceof Error ? error.message : "Failed to approve invoice");
		}
	}

	async function handleSkip() {
		try {
			await skipMutation.mutateAsync({ invoiceId });
			await utils.invoices.getById.invalidate({ id: invoiceId });
			toast.success("Invoice marked as skipped (already billed outside platform)");
		} catch (error) {
			toast.error(error instanceof Error ? error.message : "Failed to skip invoice");
		}
	}

	async function handleDelete() {
		try {
			await deleteMutation.mutateAsync({ invoiceId });
			toast.success("Invoice deleted");
		} catch (error) {
			toast.error(error instanceof Error ? error.message : "Failed to delete invoice");
		}
	}

	async function handlePushToQbo() {
		try {
			// Check if QBO already has an invoice for this period
			const history = await utils.qbo.checkQboHistory.fetch({ invoiceId });
			if (history.alreadyBilled) {
				const proceed = await new Promise<boolean>((resolve) => {
					setPushWarning({
						message: `QuickBooks already has invoice #${history.docNumber ?? history.qboInvoiceId} for this customer dated ${history.txnDate} (${formatDollars(history.totalAmount ?? 0)}). Push anyway?`,
						resolve,
					});
				});
				if (!proceed) return;
			}

			const result = await pushMutation.mutateAsync({ invoiceId });
			await utils.invoices.getById.invalidate({ id: invoiceId });
			toast.success(`Pushed to QuickBooks (QBO ID: ${result.qboInvoiceId})`);
		} catch (error) {
			toast.error(error instanceof Error ? error.message : "Failed to push to QuickBooks");
		}
	}

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-3">
					<Link
						to="/invoices"
						className="flex items-center gap-1 text-sm text-[#4f46e5] hover:underline"
					>
						<ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
						Invoices
					</Link>
					<span className="text-[#e5e7eb]">/</span>
					<h1 className="text-lg font-semibold text-[#1e1e20]">
						<Link
							to="/customers/$customerId"
							params={{ customerId: invoice.customerId }}
							className="text-[#1e1e20] hover:text-[#4f46e5] hover:underline"
						>
							{invoice.customerName}
						</Link>
					</h1>
					<span
						className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusStyles[invoice.status] ?? ""}`}
					>
						{invoice.status}
					</span>
				</div>
				<div className="flex gap-2">
					{invoice.status === "draft" && (
						<>
							<button
								type="button"
								onClick={handleDelete}
								disabled={deleteMutation.isPending}
								className="h-9 rounded border border-[#e5e7eb] bg-white px-4 text-sm font-medium text-[#6b7280] transition-colors hover:bg-[#f8f9fb] disabled:opacity-50"
							>
								Delete
							</button>
							<button
								type="button"
								onClick={handleSkip}
								disabled={skipMutation.isPending}
								className="h-9 rounded border border-[#d1d5db] bg-white px-4 text-sm font-medium text-[#6b7280] transition-colors hover:bg-[#f8f9fb] disabled:opacity-50"
							>
								{skipMutation.isPending ? "Skipping..." : "Already Billed"}
							</button>
							<button
								type="button"
								onClick={handleApprove}
								disabled={approveMutation.isPending}
								className="h-9 rounded bg-[#16a34a] px-4 text-sm font-semibold text-white transition-colors duration-150 hover:bg-[#15803d] disabled:opacity-50"
							>
								{approveMutation.isPending ? "Approving..." : "Approve"}
							</button>
						</>
					)}
					{invoice.status === "approved" && qboStatus?.connected && (
						<button
							type="button"
							onClick={handlePushToQbo}
							disabled={pushMutation.isPending}
							className="h-9 rounded bg-[#4f46e5] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#4338ca] disabled:opacity-50"
						>
							{pushMutation.isPending ? "Pushing..." : "Push to QuickBooks"}
						</button>
					)}
				</div>
			</div>

			{/* Auto-generated banner */}
			{invoice.autoGenerated && invoice.status === "draft" && (
				<div className="rounded-lg border border-[#4f46e5]/20 bg-[#eef2ff] px-4 py-3">
					<p className="text-sm text-[#4f46e5]">
						This invoice was auto-generated from document extraction. Review the line items before
						approving.
					</p>
				</div>
			)}

			{/* Info */}
			<div className="rounded-lg border border-[#e5e7eb] bg-white p-6">
				<h2 className="mb-4 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
					Invoice Details
				</h2>
				<div className="grid gap-4 sm:grid-cols-3">
					<div>
						<p className="text-xs text-[#9ca3af]">Period</p>
						<p className="text-sm font-medium text-[#1e1e20]">
							{invoice.periodStart} to {invoice.periodEnd}
						</p>
					</div>
					<div>
						<p className="text-xs text-[#9ca3af]">Created</p>
						<p className="text-sm font-medium text-[#1e1e20]">
							{invoice.createdAt ? new Date(invoice.createdAt).toLocaleDateString() : "--"}
						</p>
					</div>
					{invoice.qboInvoiceId && (
						<div>
							<p className="text-xs text-[#9ca3af]">QBO Invoice ID</p>
							<p className="text-sm font-medium text-[#1e1e20]">{invoice.qboInvoiceId}</p>
						</div>
					)}
				</div>
			</div>

			{/* Line Items — editable when not yet pushed to QBO. */}
			<LineItemsTable
				invoiceId={invoiceId}
				lineItems={invoice.lineItems}
				totalAmount={invoice.totalAmount}
				isLocked={invoice.status === "pushed" || invoice.qboInvoiceId !== null}
				onMutated={() => utils.invoices.getById.invalidate({ id: invoiceId })}
			/>

			{/* Source documents lineage — answers "where did these line items
			    come from?" without forcing a hop to /documents. The list links
			    each source document straight to its detail page. */}
			{invoice.sourceDocuments.length > 0 && (
				<div className="rounded-lg border border-[#e5e7eb] bg-white">
					<div className="border-b border-[#e5e7eb] px-6 py-4">
						<h2 className="text-xs font-medium uppercase tracking-wide text-[#6b7280]">
							Source documents
						</h2>
					</div>
					<ul className="divide-y divide-[#f3f4f6]">
						{invoice.sourceDocuments.map((doc) => (
							<li key={doc.id} className="px-6 py-3">
								<Link
									to="/documents/$documentId"
									params={{ documentId: doc.id }}
									className="inline-flex items-center gap-2 text-sm font-medium text-[#4f46e5] hover:underline"
								>
									<FileText className="h-4 w-4 shrink-0" strokeWidth={1.5} />
									<span className="truncate">{doc.fileName}</span>
								</Link>
								<p className="mt-0.5 ml-6 text-xs text-[#9ca3af]">
									{doc.payrollSystem} &middot; uploaded{" "}
									{new Date(doc.createdAt).toLocaleDateString()}
								</p>
							</li>
						))}
					</ul>
				</div>
			)}

			{/* Summary */}
			<div className="rounded-lg border border-[#e5e7eb] bg-white p-6">
				<h2 className="mb-4 text-xs font-medium uppercase tracking-wide text-[#6b7280]">Summary</h2>
				<div className="grid gap-4 sm:grid-cols-3">
					<div>
						<p className="text-xs text-[#9ca3af]">Source Charges (Provider Cost)</p>
						<p className="text-sm tabular-nums font-medium text-[#1e1e20]">
							{formatDollars(invoice.sourceChargesTotal)}
						</p>
					</div>
					<div>
						<p className="text-xs text-[#9ca3af]">Invoiced Amount (Customer Charge)</p>
						<p className="text-sm tabular-nums font-medium text-[#1e1e20]">
							{formatDollars(invoice.totalAmount)}
						</p>
					</div>
					<div>
						<p className="text-xs text-[#9ca3af]">Margin</p>
						<p
							className={`text-sm tabular-nums font-medium ${
								invoice.totalAmount - invoice.sourceChargesTotal >= 0
									? "text-[#16a34a]"
									: "text-[#dc2626]"
							}`}
						>
							{formatDollars(invoice.totalAmount - invoice.sourceChargesTotal)}
						</p>
					</div>
				</div>
			</div>

			{/* Notes */}
			{invoice.notes && (
				<div className="rounded-lg border border-[#e5e7eb] bg-white p-6">
					<h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-[#6b7280]">Notes</h2>
					<p className="text-sm text-[#1e1e20]">{invoice.notes}</p>
				</div>
			)}

			<ConfirmDialog
				open={pushWarning !== null}
				onOpenChange={(open) => {
					if (!open && pushWarning) {
						pushWarning.resolve(false);
						setPushWarning(null);
					}
				}}
				title="Push duplicate invoice?"
				description={pushWarning?.message ?? ""}
				confirmLabel="Push anyway"
				cancelLabel="Cancel"
				destructive
				onConfirm={() => {
					if (!pushWarning) return;
					pushWarning.resolve(true);
					setPushWarning(null);
				}}
			/>
		</div>
	);
}
